import React from 'react';
import { markdownToPlain } from '../../utils/answerClipboard';
import { trackChatUxEvent } from '../../utils/eventTracker';

const isChatPage = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/chat');

// 개발·테스트 전용: window.__WQ_FORCE_PART_FAILURE = 'cards,wq-chart' 처럼 부품 id 를 적으면 그 부품을 일부러 실패시킨다.
const isForcedFailure = (partId) => process.env.NODE_ENV !== 'production'
  && typeof window !== 'undefined'
  && String(window.__WQ_FORCE_PART_FAILURE || '').split(',').map((id) => id.trim()).includes(partId);

const ForcedFailure = () => {
  throw new Error('forced answer part failure');
};

const PlainFallback = ({ text }) => (
  <div className="wq-part-fallback--plain">{markdownToPlain(text) || text}</div>
);

// 대신 보일 글을 마크다운으로 그리다가도 실패하면 기호 없는 글로만 보인다.
class PlainBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <PlainFallback text={this.props.text} /> : this.props.children;
  }
}

/**
 * 부품 대신 보일 글. renderText 가 있으면 그것으로(표·목록 모양 유지), 없으면 기호 없는 글.
 * 글이 없으면 아무것도 그리지 않는다(자리표시자·JSON·빈 상자는 보이지 않는다).
 */
export const AnswerPartFallback = ({ partId, text, renderText = null }) => {
  const value = String(text || '').trim();
  if (!value) return null;
  return (
    <div className="wq-part-fallback" data-part={partId}>
      {renderText
        ? <PlainBoundary text={value}>{renderText(value)}</PlainBoundary>
        : <PlainFallback text={value} />}
    </div>
  );
};

/**
 * 부품 하나를 감싼다. 그리다 오류가 나면 답변 전체가 아니라 이 자리만 fallbackText(읽을 수 있는 글)로 바꾼다.
 * resetKey 가 바뀌면(스트리밍으로 내용이 늘어남) 다시 그려 본다.
 */
export class AnswerPartBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    if (isChatPage()) {
      trackChatUxEvent('answer_part_fallback', { stage: this.props.partId, errorCode: error?.name || 'render_failed' });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.failed && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) {
      return isForcedFailure(this.props.partId) ? <ForcedFailure /> : this.props.children;
    }
    const text = typeof this.props.fallbackText === 'function' ? this.props.fallbackText() : this.props.fallbackText;
    return <AnswerPartFallback partId={this.props.partId} renderText={this.props.renderText} text={text} />;
  }
}
